import { useMemo } from "react";
import { useCurrentPortfolio } from "./useCurrentPortfolio";
import { usePortfolioPrices } from "./usePortfolioPrices";

// This hook combines portfolio holdings data with frequent price updates
export function usePortfolioWithPrices() {
  // Get holdings data (cached for 5 minutes)
  const { 
    data: portfolioData, 
    isLoading: portfolioLoading, 
    error: portfolioError 
  } = useCurrentPortfolio();
  
  // Get price updates (refreshed every 15 seconds)
  const { 
    data: pricesData, 
    isLoading: pricesLoading, 
    error: pricesError,
    isRefetching: pricesRefetching 
  } = usePortfolioPrices();
  
  // Merge holdings with latest prices
  const mergedData = useMemo(() => {
    if (!portfolioData || !pricesData) return null;
    
    // Ensure arrays exist before mapping
    if (!portfolioData.holdings || !Array.isArray(portfolioData.holdings)) return null;
    if (!pricesData.prices || !Array.isArray(pricesData.prices)) return null;
    
    // Create a map of prices for quick lookup
    const priceMap = new Map(
      pricesData.prices.map(p => [p.ticker, p])
    );
    
    // Update holdings with latest prices
    const updatedHoldings = portfolioData.holdings.map(holding => {
      const priceData = priceMap.get(holding.ticker);
      
      if (priceData) {
        // Calculate gainLossPercent with the updated currentValue
        const updatedCurrentValue = priceData.value ?? holding.currentValue;
        const updatedGainLoss = updatedCurrentValue - holding.totalCost;
        const updatedGainLossPercent = holding.totalCost > 0 ? (updatedGainLoss / holding.totalCost) * 100 : 0;
        
        return {
          ...holding,
          currentPrice: priceData.currentPrice ?? holding.currentPrice ?? 0,
          currentValue: updatedCurrentValue,
          dayChange: priceData.dayChange ?? 0,
          dayChangePercent: priceData.dayChangePercent ?? 0,
          gainLoss: updatedGainLoss,
          gainLossPercent: updatedGainLossPercent,
        };
      }
      
      // Ensure all percentage fields are numbers, not undefined
      return {
        ...holding,
        gainLossPercent: holding.gainLossPercent ?? 0,
        dayChangePercent: holding.dayChangePercent ?? 0,
        dayChange: holding.dayChange ?? 0,
      };
    });
    
    // Calculate derived fields based on updated totalValue
    const newTotalValue = pricesData.totalValue;
    const totalCost = portfolioData.summary.totalCost;
    const totalDividends = portfolioData.summary.totalDividends;
    const totalInterest = portfolioData.summary.totalInterest;
    
    const totalGainLoss = newTotalValue - totalCost;
    const totalGainLossPercent = totalCost > 0 ? (totalGainLoss / totalCost) * 100 : 0;
    const totalReturn = totalGainLoss + totalDividends + totalInterest;
    const totalReturnPercent = totalCost > 0 ? (totalReturn / totalCost) * 100 : 0;

    // Update summary with latest totals and recalculated derived fields
    const updatedSummary = {
      ...portfolioData.summary,
      totalValue: newTotalValue,
      totalGainLoss: Math.round(totalGainLoss * 100) / 100,
      totalGainLossPercent: Math.round(totalGainLossPercent * 100) / 100,
      totalReturn: Math.round(totalReturn * 100) / 100,
      totalReturnPercent: Math.round(totalReturnPercent * 100) / 100,
      dayChange: pricesData.totalDayChange,
      dayChangePercent: pricesData.totalDayChangePercent,
      lastUpdated: pricesData.lastUpdated,
    };
    
    return {
      summary: updatedSummary,
      holdings: updatedHoldings,
    };
  }, [portfolioData, pricesData]);
  
  return {
    data: mergedData,
    isLoading: portfolioLoading || (pricesLoading && !pricesData),
    isRefetching: pricesRefetching,
    error: portfolioError || pricesError,
  };
}